import { DynamicResultTable, FormattedAddress } from '@css-xp/base';
import CheckIcon from '@atlaskit/icon/glyph/check-circle';
import Lozenge from '@atlaskit/lozenge';
import { capitalize } from 'lodash';
import React from 'react';
import { FormData } from '../subscription-txa-transfer-pane';
import { getInvoiceGroupName, formatPaymentMethod } from './billing-profile-table';
import './progress-form.scss';

type Props = {
  formState: FormData;
};

export const emptyStateOrderProp = {
  header: '',
  description: 'No billing profiles found',
  imageUrl: '',
};

export const SelectedBillingProfileTable = ({ formState }: Props) => {
  const results = [formState.selectedBillingProfile];

  const wrapInCell = (node: React.ReactElement | string) => {
    return <div className="pv-8">{node}</div>;
  };

  const getRows = () => {
    return (results ?? []).map((billingProfile, index) => {
      return {
        key: billingProfile?.id ?? index.toString(),
        cells: [
          {
            content: wrapInCell(
              billingProfile?.id ? (
                <div>
                  {getInvoiceGroupName(billingProfile?.name)}
                  {billingProfile?.defaultPaymentMethod?.paymentIssue && (
                    <Lozenge appearance="removed">PAYMENT ERROR</Lozenge>
                  )}
                </div>
              ) : (
                'Not available'
              )
            ),
          },
          {
            content: wrapInCell(
              billingProfile?.shipToParty?.id ? (
                <div>
                  <FormattedAddress
                    addressLine1={billingProfile?.shipToParty?.addressLine1}
                    addressLine2={billingProfile?.shipToParty?.addressLine2}
                    city={billingProfile?.shipToParty?.city}
                    country={billingProfile?.shipToParty?.country}
                    postcode={billingProfile?.shipToParty?.postcode}
                    state={billingProfile?.shipToParty?.state}
                    organizationName={billingProfile?.shipToParty?.name}
                  />
                </div>
              ) : (
                'Not available'
              )
            ),
          },
          {
            content: wrapInCell(
              billingProfile?.shipToParty?.taxId ? billingProfile?.shipToParty?.taxId : 'Not provided'
            ),
          },
          {
            content: billingProfile?.defaultPaymentMethod
              ? formatPaymentMethod(billingProfile?.defaultPaymentMethod)
              : 'Not available',
          },
          {
            content: wrapInCell(
              billingProfile?.shipToPriceEligibility ? (
                capitalize(billingProfile?.shipToPriceEligibility)
              ) : (
                <div aria-label="Not applicable">-</div>
              )
            ),
          },
          {
            content: wrapInCell(billingProfile?.currency ?? 'N/A'),
          },
        ],
      };
    });
  };

  const getHead = () => {
    const cells = [
      {
        content: 'Name',
        width: 20,
      },
      {
        content: 'Sold-to address',
        width: 23,
      },
      {
        content: 'Tax ID',
        width: 15,
      },
      {
        content: 'Payment Method',
        width: 20,
      },
      {
        content: 'Pricing',
        width: 10,
      },
      {
        content: 'Currency',
        width: 10,
      },
    ];

    return {
      cells,
    };
  };

  return (
    <div className="plan-view selected-table mt-40" data-testid="select-billing-profile-view">
      <h2>
        <CheckIcon label="" size="medium" testId="icon-bill-admin" /> Billing profile{' '}
      </h2>
      <DynamicResultTable
        head={getHead()}
        rows={getRows()}
        testId="contact-billing-accounts"
        emptyStateProps={emptyStateOrderProp}
        boxShadow={false}
      />
    </div>
  );
};
